import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../../models/stores/Root';
import { SelectOption } from '../../../types/common';
import LabeledCheckbox from '../../input/LabeledCheckbox';
import { AddressContract, AddressFormData } from '../../../types/Address';
import SectionTitle from '../../content/SectionTitle';
import CustomMessage from '../../content/CustomMessage';
import ShipToSummary from '../../summary/ShipToSummary';
import { SectionSummaryGroupPosition } from '../../summary/SectionSummary';
import AddressFormWithButtons from '../../input/AddressFormWithButtons';
import ClosableModal from '../ClosableModal';
import Button from '../../input/Button';
import { AddressAutocompleteDetailsContract } from '../../../types/AddressValidation';

export const GuestShippingSectionTestId = 'GuestShippingSection-framework';
export const GuestShippingSectionUnverifiedModalTestId =
  'GuestShippingSection-unverified-modal';
export const GuestShippingSectionSuggestChangeModalTestId =
  'GuestShippingSection-suggest-change-modal';

export type GuestShippingSectionProps = {
  onValidData?: (formData: AddressFormData) => void;
  onErrorStatus?: (hasErrors: boolean) => void;
  onMissingContactData?: () => void;
  displaySummary?: boolean;
  groupPosition?: SectionSummaryGroupPosition;
  onShipToInfoEdit?: () => void;
  canContinue?: boolean;
  isBusy?: boolean;
};

const GuestShippingSection: React.FC<GuestShippingSectionProps> = observer(
  ({
    displaySummary = false,
    groupPosition,
    onValidData,
    onErrorStatus,
    onMissingContactData,
    onShipToInfoEdit,
    canContinue = true,
    isBusy = false,
  }: GuestShippingSectionProps) => {
    const rootStore = useMst();
    const {
      configuration,
      cart,
      validation: { addressAutocomplete, addressVerification },
    } = rootStore;
    const [isSaving, setIsSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isVerified, setIsVerified] = useState(true);
    const [missingAddress2ShownCount, setMissingAddress2ShownCount] =
      useState(0);
    const [defaultShippingValues, setDefaultShippingValues] = useState<
      AddressFormData | AddressContract
    >({
      firstName: cart.shippingAddress.firstName ?? undefined,
      lastName: cart.shippingAddress.lastName ?? undefined,
      address1: cart.shippingAddress.address1 ?? undefined,
      address2: cart.shippingAddress.address2 ?? undefined,
      city: cart.shippingAddress.city ?? undefined,
      state: cart.shippingAddress.state ?? undefined,
      postalCode: cart.shippingAddress.postalCode ?? undefined,
      country: cart.shippingAddress.country ?? configuration.defaultCountry,
      phoneNumber: cart.shippingAddress.phoneNumber ?? undefined,
      companyName: cart.shippingAddress.companyName ?? undefined,
      residential: cart.shippingAddress.residential ?? true,
    });

    const countryOptions = configuration.countriesAsSelectOptions;

    const handleOnErrorStatus = (hasErrors: boolean): void => {
      onErrorStatus?.(hasErrors);
    };

    const handleGetStateOptions = (
      country: string
    ): { options: SelectOption[]; label: string } => {
      const options = configuration.getCurrentStatesAsSelectOptions(country);
      const label = configuration.getStateLabel(country);
      return { options, label };
    };

    const handleEmailOptInOnChange = (
      event: React.ChangeEvent<HTMLInputElement>
    ): void => {
      event.persist();
      cart.updateCustomer({ emailOptIn: event.target.checked });
    };

    const shouldUpdateShippingMethods = (
      formData: AddressFormData
    ): boolean => {
      return (
        formData.address1 !== defaultShippingValues.address1 ||
        formData.address2 !== defaultShippingValues.address2 ||
        formData.city !== defaultShippingValues.city ||
        formData.state !== defaultShippingValues.state ||
        formData.country !== defaultShippingValues.country ||
        formData.postalCode !== defaultShippingValues.postalCode ||
        formData.residential !== defaultShippingValues.residential
      );
    };

    const handleOnValidAddressData = async (
      formData: AddressFormData,
      bypassAddressVerification = false
    ): Promise<void> => {
      if (!canContinue) {
        onMissingContactData?.();
        return;
      }
      setIsSaving(true);
      cart.updateCustomer({ phoneNumber: formData.phoneNumber ?? undefined });
      const updatedAddress = {
        id: null,
        preferred: true,
        lastName: formData.lastName ?? null,
        firstName: formData.firstName ?? null,
        address1: formData.address1 ?? null,
        address2: formData.address2 ?? null,
        city: formData.city ?? null,
        state: formData.state ?? null,
        postalCode: formData.postalCode ?? null,
        country: formData.country ?? null,
        phoneNumber: formData.phoneNumber ?? null,
        companyName: formData.companyName ?? null,
        residential: formData.residential ?? null,
      };

      const missingAddress2CountButNotReally =
        missingAddress2ShownCount > 0 && !!updatedAddress.address2;
      const missingAddress2ShownCountZero = missingAddress2ShownCount === 0;
      const performAddressVerification =
        addressVerification.isEnabled &&
        !bypassAddressVerification &&
        (missingAddress2ShownCountZero || missingAddress2CountButNotReally);

      if (performAddressVerification) {
        try {
          await addressVerification.verifyAddressFlow(updatedAddress);

          if (addressVerification.missingAddress2) {
            setMissingAddress2ShownCount(missingAddress2ShownCount + 1);
          }

          if (!addressVerification.completelyVerifiedAddress) {
            setIsVerified(false);
            setIsSaving(false);
            setIsOpen(!addressVerification.missingAddress2);
            return;
          }
        } catch (err) {
          // TODO: Do something with this error
          // eslint-disable-next-line no-console
          console.error(err);
        }
      }

      setIsVerified(true);
      setIsOpen(false);
      cart.updateShippingAddress(updatedAddress);
      rootStore.send({ type: 'SHIPPING_ADDRESS_SAVED' });
      setDefaultShippingValues(updatedAddress as AddressFormData);

      if (
        shouldUpdateShippingMethods(updatedAddress as AddressFormData) ||
        missingAddress2ShownCount
      ) {
        rootStore.send({ type: 'SHIPPING_ADDRESS_CHANGED' });
        cart.setFetchingShippingMethods(true);
        await cart.updateCartFlow();
        cart.setFetchingShippingMethods(false);
      }
      rootStore.send({ type: 'SHIPPING_METHODS_FETCHED' });

      setIsSaving(false);
      onValidData?.(formData);
    };

    useEffect(() => {
      if (!defaultShippingValues.address1 && cart.shippingAddress.address1) {
        setDefaultShippingValues(cart.cartData.shippingAddress);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultShippingValues, cart.shippingAddress.address1]);

    const handleOnShipToInfoEdit = (): void => {
      setMissingAddress2ShownCount(0);
      onShipToInfoEdit?.();
    };

    const handleAutocompleteData = async (address1: string): Promise<void> => {
      await addressAutocomplete.getMatchesFlow(address1);
    };

    const handleAutocompleteSelect = async (
      placeId: string
    ): Promise<AddressAutocompleteDetailsContract | null> => {
      return addressAutocomplete.getMatchDetails(placeId);
    };

    const handleAutocompleteClear = (): void => {
      addressAutocomplete.clearMatches();
    };

    return displaySummary ? (
      <ShipToSummary
        address={cart.shippingAddress}
        onEditClicked={handleOnShipToInfoEdit}
        groupPosition={groupPosition}
        toolTipKey="GuestShippingShipToSummaryKey"
      />
    ) : (
      <div
        data-testid={GuestShippingSectionTestId}
        className={configuration.features.enableEmailOptIn ? '' : 'mb-6'}
      >
        <SectionTitle>Shipping Address</SectionTitle>
        {!!configuration.messages.shipTo && (
          <CustomMessage customMessage={configuration.messages.shipTo} />
        )}
        <AddressFormWithButtons
          isShippingAddress
          countryOptions={countryOptions}
          defaultValues={defaultShippingValues as AddressFormData}
          getStateConfig={handleGetStateOptions}
          onSubmitClicked={handleOnValidAddressData}
          onErrorStatus={handleOnErrorStatus}
          isSaving={isSaving || isBusy}
          submitButtonText="Continue"
          missingAddress2={
            missingAddress2ShownCount > 0 && addressVerification.missingAddress2
          }
          autocompleteProps={{
            onAutocompleteClear: handleAutocompleteClear,
            onAutocompleteData: handleAutocompleteData,
            onAutocompleteSelect: handleAutocompleteSelect,
            matches: addressAutocomplete.matchList,
          }}
          shippingRatesAddressType={
            configuration.features.shippingRatesAddressType
          }
        />
        {configuration.features.enableEmailOptIn && (
          <LabeledCheckbox
            name="emailOptIn"
            renderChild
            className="mb-6"
            label={
              configuration.labels.emailOptIn ??
              'Send me exclusive offers via email'
            }
            defaultChecked={cart.customer.emailOptIn}
            onChange={handleEmailOptInOnChange}
          />
        )}
        {!isVerified && (
          <ClosableModal
            className="w-full rounded-3xl h-full mt-20 md:w-1/2 md:max-w-xl md:h-auto md:rounded md:mt-0"
            isOpen={isOpen}
            onRequestClose={(): void => setIsOpen(false)}
            label="Is this address correct?"
          >
            {addressVerification.basicVerifiedAddress && (
              <div data-testid={GuestShippingSectionSuggestChangeModalTestId}>
                <div className="pb-2 pl-2">Suggested</div>
                <ShipToSummary
                  address={addressVerification.verificationAddressSummary}
                  toolTipKey="GuestShippingAddressVerifciationShipToSummaryKey"
                />
                <Button
                  onClick={(): Promise<void> =>
                    handleOnValidAddressData(
                      addressVerification.verificationAddressSummary,
                      true
                    )
                  }
                  className="mt-2"
                >
                  Yes
                </Button>
                <div className="pb-2 pl-2 mt-6">Original</div>
                <ShipToSummary
                  address={addressVerification.customerAddressSummary}
                  onEditClicked={(): void => setIsOpen(false)}
                  toolTipKey="GuestShippingOriginalShipToSummaryKey"
                />
                <Button
                  onClick={(): Promise<void> =>
                    handleOnValidAddressData(
                      addressVerification.customerAddressSummary,
                      true
                    )
                  }
                  className="mt-2"
                  secondary
                >
                  No, Keep Original
                </Button>
              </div>
            )}
            {(addressVerification.unverifiedAddress ||
              addressVerification.unrecognizedAddress2 ||
              addressVerification.partiallyVerifiedNoRecommendations) && (
              <div data-testid={GuestShippingSectionUnverifiedModalTestId}>
                <div className="customer-login-prompt text-sm text-left mx-2 mb-2 pl-4 py-2 pr-6">
                  Your address was not recognized by USPS address validation.
                  That’s ok, but please double check it, and click{' '}
                  <strong>Edit</strong> to make changes, or proceed as-is.
                </div>
                <ShipToSummary
                  address={addressVerification.customerAddressSummary}
                  onEditClicked={(): void => setIsOpen(false)}
                  toolTipKey="GuestShippingUnreconizedShipToSummaryKey"
                />
                <Button
                  onClick={(): Promise<void> =>
                    handleOnValidAddressData(
                      addressVerification.customerAddressSummary,
                      true
                    )
                  }
                  className="mt-2"
                >
                  Use This Address
                </Button>
              </div>
            )}
          </ClosableModal>
        )}
      </div>
    );
  }
);

export default GuestShippingSection;
